import {
  faCar,
  faCheck,
  faExternalLink,
  faFire,
  faGlassWater,
  faHouseMedical,
  faToiletPortable,
  faTree,
  faXmark,
  type IconDefinition,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { memo, type ReactNode, useRef, useState } from "react";
import { Card, Col, Row, Stack } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { swisstopoUrl } from "../services/coords";
import type {
  CampsiteTraitBase,
  CampsiteTraitDistance,
  CampsiteTraits,
  GetCampsiteQuery,
  ReportInput,
} from "../types/graphql/graphql";
import { CampsiteAvailability } from "./CampsiteAvailabililty";
import type { CampsiteCardTab } from "./CampsiteCard";
import styles from "./CampsiteDetail.module.css";
import CampsitePhotos from "./CampsitePhotos";
import { CampsiteReports, type ReportsTabMode } from "./CampsiteReports";

type CampsiteAttribute = {
  trait: CampsiteTraitBase | CampsiteTraitDistance;
  label: string;
  icon: IconDefinition;
};

export type CampsiteDetailProps = {
  id?: number;
  campsiteDetail: GetCampsiteQuery["campsite"];
  selectedTab: CampsiteCardTab;
  onSelectedTabChange: (tab: CampsiteCardTab) => void;
  onReport?: (report: ReportInput) => void;
};

export const CampsiteDetail = memo(function CampsiteDetail({
  campsiteDetail,
  onReport,
  selectedTab,
  onSelectedTabChange,
}: CampsiteDetailProps) {
  const { t } = useTranslation();

  const expanded = !!campsiteDetail;
  const {
    description,
    website,
    costs,
    remarks,
    specialities,
    owner,
    upcomingSeasons,
    maxHeadcount,
    reports,
    traits,
    lv03Coordinates,
  } = campsiteDetail;
  const { drinkingWater, openFiresAllowed, latrinesAllowed, firewood, emergencyAccomodation, carAccess } = traits ?? {};
  const detailAttributes: { [key in keyof CampsiteTraits]?: CampsiteAttribute } = {
    drinkingWater: {
      trait: drinkingWater,
      icon: faGlassWater,
      label: t("activerecord.attributes.campsite.drinking_water"),
    },
    openFiresAllowed: {
      trait: openFiresAllowed,
      icon: faFire,
      label: t("activerecord.attributes.campsite.open_fires_allowed"),
    },
    latrinesAllowed: {
      trait: latrinesAllowed,
      icon: faToiletPortable,
      label: t("activerecord.attributes.campsite.latrines_allowed"),
    },
    firewood: {
      trait: firewood,
      icon: faTree,
      label: t("activerecord.attributes.campsite.firewood"),
    },
    emergencyAccomodation: {
      trait: emergencyAccomodation,
      icon: faHouseMedical,
      label: t("activerecord.attributes.campsite.emergency_accomodation"),
    },
    carAccess: {
      trait: carAccess,
      icon: faCar,
      label: t("activerecord.attributes.campsite.car_access"),
    },
  };

  const [commentTabMode, setReportTabMode] = useState<ReportsTabMode>("list");
  const ref = useRef<HTMLDivElement>(null);

  if (!campsiteDetail) return;
  if (expanded) ref.current?.scrollIntoView();

  const tabContent: Record<CampsiteCardTab, ReactNode> = {
    info: (
      <Card.Body>
        <Stack gap={5}>
          <div className={styles.traitsGrid}>
            {Object.entries(detailAttributes).map(([key, { icon, label, trait }]) => (
              <div className="bg-white-50 rounded p-3" key={key}>
                <div className="d-flex align-items-baseline gap-2 justify-content-between fw-bold">
                  <FontAwesomeIcon icon={icon} />
                  <span className="flex-grow-1">{label}</span>
                  <FontAwesomeIcon
                    icon={trait.applies ? faCheck : faXmark}
                    className={trait.applies ? "text-success" : "text-danger"}
                  />
                </div>
                {trait.remarks ?? ""}
              </div>
            ))}
          </div>
          <div>
            {!!maxHeadcount && (
              <Row className="mb-2">
                <Col className="fw-bold" sm={3}>
                  {t("activerecord.attributes.campsite.max_headcount")}
                </Col>
                <Col className="text-start mb-0">{maxHeadcount}</Col>
              </Row>
            )}
            {!!website && (
              <Row className="mb-2">
                <Col className="fw-bold" sm={3}>
                  {t("activerecord.attributes.campsite.website")}
                </Col>
                <Col className="text-start mb-0">
                  <a href={website} rel="noreferrer" target="_blank">
                    {website}
                  </a>
                </Col>
              </Row>
            )}
            {!!lv03Coordinates && (
              <Row className="mb-2">
                <Col className="fw-bold" sm={3}>
                  {t("activerecord.attributes.campsite.lv03_coordinates")}
                </Col>
                <Col className="text-start mb-0">
                  <a target="_blank" rel="noopener noreferrer" href={swisstopoUrl(lv03Coordinates)}>
                    {lv03Coordinates}
                    <FontAwesomeIcon className="ms-2" icon={faExternalLink} />
                  </a>
                </Col>
              </Row>
            )}
            {!!costs && (
              <Row className="mb-2">
                <Col className="fw-bold" sm={3}>
                  {t("activerecord.attributes.campsite.costs")}
                </Col>
                <Col className="text-start mb-0">{costs}</Col>
              </Row>
            )}
            {!!remarks && (
              <Row className="mb-2">
                <Col className="fw-bold" sm={3}>
                  {t("activerecord.attributes.campsite.remarks")}
                </Col>
                <Col className="text-start mb-0">{remarks}</Col>
              </Row>
            )}
            {!!specialities && (
              <Row className="mb-2">
                <Col className="fw-bold" sm={3}>
                  {t("activerecord.attributes.campsite.specialities")}
                </Col>
                <Col className="text-start mb-0">{specialities}</Col>
              </Row>
            )}
          </div>

          {description && <div className="pb-4">{description}</div>}
        </Stack>
      </Card.Body>
    ),
    availability: (
      <CampsiteAvailability
        campsite={campsiteDetail}
        owner={owner ?? undefined}
        seasons={upcomingSeasons ?? []}
        reports={reports ?? []}
        campsiteId={campsiteDetail.id}
        onReport={() => {
          onSelectedTabChange("reports");
          setReportTabMode("reporting");
        }}
      />
    ),
    reports: (
      <CampsiteReports
        onReport={onReport}
        reports={reports ?? []}
        mode={commentTabMode}
        campsiteId={campsiteDetail.id}
        onModeChange={setReportTabMode}
      />
    ),
    photos: <CampsitePhotos campsiteId={campsiteDetail.id} />,
  };

  return tabContent[selectedTab];
});
